import React, { useEffect, useState } from "react";
import { ymd } from "../lib/dates";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, ClipboardText, Warning, CheckCircle,
  Clock, ArrowsClockwise, Buildings, User, CalendarDots, Flag
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError, formatDate } from "../lib/api";

const PRIORITY_META = {
  rendah: { label: "Rendah", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  sedang: { label: "Sedang", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  tinggi: { label: "Tinggi", cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" },
};

const STATUS_META = {
  todo: { label: "To Do", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  in_progress: { label: "Berjalan", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  done: { label: "Selesai", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  cancelled: { label: "Dibatalkan", cls: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
};

const EMPTY_FORM = {
  title: "",
  agenda_date: ymd(),
  due_date: "",
  priority: "sedang",
  status: "todo",
  assignee_name: "",
  lembaga_id: "",
  notes: "",
  outcome: "",
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function StatCard({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default: "bg-card border-border",
    warn: "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800",
    danger: "bg-rose-50 border-rose-200 dark:bg-rose-900/20 dark:border-rose-800",
    ok: "bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800",
    info: "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</div>
        <Icon size={18} weight="duotone" className="text-foreground/60" />
      </div>
      <div className="font-mono text-2xl font-bold text-foreground">{value}</div>
    </div>
  );
}

export default function AgendaManagerKeuangan() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [lembagas, setLembagas] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (priorityFilter !== "all") params.set("priority", priorityFilter);
      const [list, sum, lem] = await Promise.all([
        api.get(`/finance-agendas${params.toString() ? `?${params}` : ""}`),
        api.get("/finance-agendas/summary"),
        api.get("/lembaga").catch(() => ({ data: [] })),
      ]);
      setItems(list.data);
      setSummary(sum.data);
      setLembagas(lem.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat agenda");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, priorityFilter]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, agenda_date: ymd() });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      title: a.title || "",
      agenda_date: a.agenda_date || "",
      due_date: a.due_date || "",
      priority: a.priority || "sedang",
      status: a.status || "todo",
      assignee_name: a.assignee_name || "",
      lembaga_id: a.lembaga_id || "",
      notes: a.notes || "",
      outcome: a.outcome || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Judul agenda wajib diisi");
    if (!form.agenda_date) return toast.error("Tanggal agenda wajib diisi");
    try {
      const payload = {
        ...form,
        due_date: form.due_date || null,
        lembaga_id: form.lembaga_id || null,
        assignee_name: form.assignee_name || null,
        notes: form.notes || null,
        outcome: form.outcome || null,
      };
      if (editing) await api.put(`/finance-agendas/${editing.id}`, payload);
      else await api.post("/finance-agendas", payload);
      toast.success("Agenda tersimpan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan agenda");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus agenda ini?")) return;
    try {
      await api.delete(`/finance-agendas/${id}`);
      toast.success("Agenda terhapus");
      load();
    } catch {
      toast.error("Gagal menghapus agenda");
    }
  };

  const quickStatus = async (a, newStatus) => {
    try {
      await api.patch(`/finance-agendas/${a.id}/status`, { status: newStatus });
      toast.success("Status diperbarui");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal update status");
    }
  };

  const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));

  return (
    <div data-testid="agenda-manager-keuangan-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Super Admin</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black text-foreground tracking-tight">
            Agenda Manager Keuangan
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Rekam, monitor, dan tindak lanjuti pekerjaan bagian keuangan yayasan.
          </p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="agenda-add-btn">
          <Plus size={16} weight="bold" /> Tambah Agenda
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard testid="agenda-stat-total" icon={ClipboardText} label="Total" value={summary?.total || 0} />
        <StatCard testid="agenda-stat-todo" icon={Clock} label="To Do" value={summary?.todo || 0} tone="info" />
        <StatCard testid="agenda-stat-progress" icon={ArrowsClockwise} label="Berjalan" value={summary?.in_progress || 0} tone="warn" />
        <StatCard testid="agenda-stat-overdue" icon={Warning} label="Terlambat" value={summary?.overdue || 0} tone="danger" />
        <StatCard testid="agenda-stat-done" icon={CheckCircle} label="Selesai" value={summary?.done || 0} tone="ok" />
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Status:</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="agenda-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="todo">To Do</SelectItem>
              <SelectItem value="in_progress">Berjalan</SelectItem>
              <SelectItem value="done">Selesai</SelectItem>
              <SelectItem value="cancelled">Dibatalkan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Prioritas:</Label>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="agenda-filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="tinggi">Tinggi</SelectItem>
              <SelectItem value="sedang">Sedang</SelectItem>
              <SelectItem value="rendah">Rendah</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Memuat agenda...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <ClipboardText size={40} className="mx-auto text-muted-foreground mb-2" weight="duotone" />
            <div className="text-sm text-muted-foreground">Belum ada agenda tercatat.</div>
            <Button variant="outline" size="sm" onClick={openNew} className="mt-3 gap-2" data-testid="agenda-empty-add-btn">
              <Plus size={14} /> Tambah agenda pertama
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((a) => {
              const dUntil = daysUntil(a.due_date);
              const overdue = a.due_date && dUntil < 0 && !["done", "cancelled"].includes(a.status);
              const soon = a.due_date && dUntil >= 0 && dUntil <= 3 && !["done", "cancelled"].includes(a.status);
              return (
                <li key={a.id} className="p-4 hover:bg-muted/40 transition-colors" data-testid={`agenda-item-${a.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PRIORITY_META[a.priority]?.cls}`}>
                          <Flag size={10} className="inline -mt-0.5" weight="fill" /> {PRIORITY_META[a.priority]?.label}
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_META[a.status]?.cls}`}>
                          {STATUS_META[a.status]?.label}
                        </span>
                        {overdue && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white">
                            TERLAMBAT {Math.abs(dUntil)} hari
                          </span>
                        )}
                        {soon && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                            {dUntil === 0 ? "HARI INI" : `H-${dUntil}`}
                          </span>
                        )}
                      </div>
                      <div className="font-semibold text-foreground truncate">{a.title}</div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1"><CalendarDots size={13} /> {formatDate(a.agenda_date)}</span>
                        {a.due_date && <span className="flex items-center gap-1"><Clock size={13} /> Deadline: {formatDate(a.due_date)}</span>}
                        {a.assignee_name && <span className="flex items-center gap-1"><User size={13} /> {a.assignee_name}</span>}
                        {a.lembaga_id && lembagaMap[a.lembaga_id] && (
                          <span className="flex items-center gap-1"><Buildings size={13} /> {lembagaMap[a.lembaga_id]}</span>
                        )}
                      </div>
                      {a.notes && <div className="text-xs text-foreground/70 mt-1.5 line-clamp-2">{a.notes}</div>}
                      {a.outcome && (
                        <div className="text-xs text-emerald-700 dark:text-emerald-400 mt-1.5">
                          <span className="font-semibold">Tindak lanjut:</span> {a.outcome}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <Select value={a.status} onValueChange={(v) => quickStatus(a, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`agenda-status-${a.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todo">To Do</SelectItem>
                          <SelectItem value="in_progress">Berjalan</SelectItem>
                          <SelectItem value="done">Selesai</SelectItem>
                          <SelectItem value="cancelled">Dibatalkan</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`agenda-edit-${a.id}`} title="Edit">
                        <PencilSimple size={16} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(a.id)} data-testid={`agenda-delete-${a.id}`} title="Hapus">
                        <Trash size={16} className="text-rose-600" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Agenda" : "Tambah Agenda Manager Keuangan"}</DialogTitle>
            <DialogDescription>
              Rekam pekerjaan/pekerjaan Manager Keuangan lengkap dengan tanggal, deadline, prioritas, dan tindak lanjut.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Judul Agenda *</Label>
              <Input
                data-testid="agenda-input-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Contoh: Rekonsiliasi kas bulan Februari"
              />
            </div>
            <div>
              <Label>Tanggal Agenda *</Label>
              <Input
                type="date"
                data-testid="agenda-input-date"
                value={form.agenda_date}
                onChange={(e) => setForm({ ...form, agenda_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Deadline (opsional)</Label>
              <Input
                type="date"
                data-testid="agenda-input-due"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Prioritas</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="agenda-input-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rendah">Rendah</SelectItem>
                  <SelectItem value="sedang">Sedang</SelectItem>
                  <SelectItem value="tinggi">Tinggi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="agenda-input-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">To Do</SelectItem>
                  <SelectItem value="in_progress">Berjalan</SelectItem>
                  <SelectItem value="done">Selesai</SelectItem>
                  <SelectItem value="cancelled">Dibatalkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>PIC / Manager Keuangan</Label>
              <Input
                data-testid="agenda-input-assignee"
                value={form.assignee_name}
                onChange={(e) => setForm({ ...form, assignee_name: e.target.value })}
                placeholder="Nama penanggung jawab"
              />
            </div>
            <div>
              <Label>Lembaga (opsional)</Label>
              <Select value={form.lembaga_id || "none"} onValueChange={(v) => setForm({ ...form, lembaga_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="agenda-input-lembaga"><SelectValue placeholder="Yayasan / Semua" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Yayasan (umum)</SelectItem>
                  {lembagas.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Catatan / Instruksi</Label>
              <Textarea
                data-testid="agenda-input-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Instruksi detail, target, referensi dokumen, dll."
              />
            </div>
            <div className="md:col-span-2">
              <Label>Tindak Lanjut / Hasil (opsional)</Label>
              <Textarea
                data-testid="agenda-input-outcome"
                rows={2}
                value={form.outcome}
                onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                placeholder="Diisi ketika ada progres atau hasil akhir dari manager keuangan."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="agenda-cancel-btn">Batal</Button>
            <Button onClick={save} data-testid="agenda-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
